import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users2, Loader2, MessageCircle, Bell, Send, Award } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
// Re-cableado a lealify-base (/analytics/rfm) — reemplaza Elasticsearch.
import { analyticsApi } from '../../../api/analyticsApi';
import type { RfmSegments } from '../../../api/analyticsApi';
import { RUTA_CAMPANAS, prefillReactivacion, prefillMejoresClientes } from '../utils/campaignActions';

// Orden y color de cada segmento (de mejor a peor)
const SEG_ORDER: Array<{ key: string; color: string }> = [
  { key: 'Campeones', color: '#8b5cf6' },
  { key: 'Leales', color: '#3b82f6' },
  { key: 'Nuevos/Prometedores', color: '#10b981' },
  { key: 'En riesgo', color: '#f59e0b' },
  { key: 'Por dormir', color: '#f97316' },
  { key: 'Por recuperar', color: '#64748b' },
];

const waLink = (countryCode: string, phone: string) => {
  const digits = `${countryCode || ''}${phone || ''}`.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '#';
};

/**
 * Segmentos RFM (Recencia + Frecuencia) + lista de clientes a reactivar.
 * Fase 3. La base se normaliza por "clientes vivos" (visitaron en <=180d);
 * los importados históricos se cuentan aparte para no distorsionar.
 */
const RfmSegmentsPanel: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<RfmSegments | null>(null);
  const [loading, setLoading] = useState(true);

  const lanzarCampana = (prefill: any) => navigate(RUTA_CAMPANAS, { state: { prefill } });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!currentUser?.businessId) return;
      setLoading(true);
      try {
        const d = await analyticsApi.rfm();
        if (alive) setData(d);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [currentUser?.businessId, currentUser?.timeZone]);

  if (loading) {
    return (
      <div className="bg-surface shadow rounded-lg p-6 border border-line flex items-center justify-center min-h-[200px]">
        <Loader2 className="animate-spin h-5 w-5 text-blue-500 mr-3" />
        <span className="text-content-3 text-sm">Segmentando a tus clientes...</span>
      </div>
    );
  }

  if (!data || data.baseViva === 0) {
    return (
      <div className="bg-surface shadow rounded-lg p-6 border border-line">
        <h3 className="text-sm font-medium text-content-2 mb-3 flex items-center">
          <Users2 className="w-4 h-4 mr-2 text-purple-500" /> Segmentos de clientes
        </h3>
        <p className="text-sm text-content-3 text-center py-8">
          Aún no hay suficientes clientes activos para segmentar. Sigue registrando visitas.
        </p>
      </div>
    );
  }

  const total = data.baseViva || 1;
  const segs = SEG_ORDER.filter((s) => (data.segments[s.key] || 0) > 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Segmentos */}
      <div className="bg-surface shadow rounded-lg p-6 border border-line">
        <h3 className="text-sm font-medium text-content-2 mb-1 flex items-center">
          <Users2 className="w-4 h-4 mr-2 text-purple-500" /> Segmentos de clientes
        </h3>
        <p className="text-[11px] text-content-3 mb-4">
          Sobre {data.baseViva.toLocaleString('es-MX')} clientes activos (visitaron en 180 días).
          {data.historicoInactivo > 0 && ` ${data.historicoInactivo.toLocaleString('es-MX')} históricos inactivos no se cuentan.`}
        </p>
        <div className="space-y-2.5">
          {segs.map((s) => {
            const v = data.segments[s.key] || 0;
            const pct = (v / total) * 100;
            return (
              <div key={s.key} className="flex items-center gap-3 text-sm">
                <div className="w-36 text-content-2 truncate">{s.key}</div>
                <div className="flex-1 h-4 rounded bg-surface-2 overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${Math.max(3, pct)}%`, background: s.color }} />
                </div>
                <div className="w-8 text-right font-semibold text-content">{v}</div>
              </div>
            );
          })}
        </div>
        {(data.championIds?.length || 0) > 0 && (
          <button onClick={() => lanzarCampana(prefillMejoresClientes(data.championIds))}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-accent-solid hover:bg-accent-solid/90 text-white text-sm font-medium px-4 py-2 rounded-md">
            <Award className="h-4 w-4" /> Premiar a mis {data.championIds.length} mejores clientes
          </button>
        )}
      </div>

      {/* Clientes a reactivar */}
      <div className="bg-surface shadow rounded-lg p-6 border border-line">
        <h3 className="text-sm font-medium text-content-2 mb-1 flex items-center">
          <Bell className="w-4 h-4 mr-2 text-amber-500" /> Clientes a reactivar
        </h3>
        <p className="text-[11px] text-content-3 mb-3">
          Buenos clientes que se están enfriando (con puntos sin usar). Un mensaje puede recuperarlos.
        </p>
        {data.reactivar.length === 0 ? (
          <p className="text-sm text-content-3 text-center py-6">Ningún cliente en riesgo con puntos pendientes. ¡Buen trabajo!</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-content-3 text-[11px] uppercase">
                  <th className="text-left font-semibold py-1.5 pr-2">Cliente</th>
                  <th className="text-right font-semibold py-1.5 px-2">Sin venir</th>
                  <th className="text-right font-semibold py-1.5 px-2">Puntos</th>
                  <th className="py-1.5" />
                </tr>
              </thead>
              <tbody>
                {data.reactivar.map((r, i) => (
                  <tr key={i} className="border-t border-line">
                    <td className="py-2 pr-2">
                      <div className="text-content font-medium truncate max-w-[140px]">{r.name}</div>
                      <div className="text-[11px] text-content-3">{r.visits} visita{r.visits !== 1 ? 's' : ''}</div>
                    </td>
                    <td className="py-2 px-2 text-right text-content-2 whitespace-nowrap">{r.days} días</td>
                    <td className="py-2 px-2 text-right font-semibold text-content">{r.points}</td>
                    <td className="py-2 text-right">
                      {r.phone ? (
                        <a href={waLink(r.countryCode, r.phone)} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white text-xs font-medium px-2.5 py-1 rounded-md">
                          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                        </a>
                      ) : <span className="text-[11px] text-content-3">sin tel.</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(data.reactivarIds?.length || 0) > 0 && (
          <button onClick={() => lanzarCampana(prefillReactivacion(data.reactivarIds))}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-accent-solid hover:bg-accent-solid/90 text-white text-sm font-medium px-4 py-2 rounded-md">
            <Send className="h-4 w-4" /> Lanzar campaña a estos {data.reactivarIds.length} clientes
          </button>
        )}
      </div>
    </div>
  );
};

export default RfmSegmentsPanel;
